import { create } from 'zustand'

import { ApiError } from '@/lib/api'
import { clearTokens, decodeUser, readTokens, writeTokens } from '@/lib/auth'

/**
 * Auth store — single source of truth for who the user is and the
 * tokens they hold.
 *
 * Why Zustand and not React Context
 * ---------------------------------
 * The auth state crosses every layer of the app (route guard, API
 * client, header, dashboard hooks, …). A React Context would
 * re-render every consumer on every token rotation; Zustand
 * subscribes per-slice so a token refresh doesn't invalidate the
 * dashboard. Zustand is also accessible OUTSIDE the React tree —
 * the API client below reads `useAuthStore.getState()` to grab
 * the current token without needing to be re-instantiated when
 * tokens rotate.
 *
 * Hydration
 * ---------
 * On boot, `hydrate()` reads `localStorage` and decides:
 *   - Both tokens missing or invalid → state stays "logged out".
 *   - Access token still valid → user is in.
 *   - Access expired but refresh present → try a silent refresh.
 *     Failure → logged out.
 *
 * The race-condition-free flow is: the app's root component calls
 * `hydrate()` once in a `useEffect` and sets `hydrated` to true
 * when done. Routes block on that flag so they don't redirect to
 * /login while the refresh is in flight.
 */

const AUTH_BASE: string =
  (import.meta.env.VITE_API_URL as string | undefined) ?? ''

export interface AuthUser {
  id: string
  tenantId: string
  role: 'super_admin' | 'admin' | 'driver'
  /**
   * Display fields populated from the login response (the JWT
   * itself does not carry name/email). Stay null until the next
   * successful login if the page is reloaded — the dashboard
   * tolerates that.
   */
  name: string | null
  email: string | null
}

interface AuthState {
  user: AuthUser | null
  accessToken: string | null
  refreshToken: string | null
  /** True once `hydrate()` has finished — guards initial routing. */
  hydrated: boolean

  isAuthenticated: () => boolean
  hydrate: () => Promise<void>
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  /**
   * Performs a refresh and returns the new access token. Used by
   * the API client; throws on failure.
   */
  refresh: () => Promise<string>
}

/* -------------------------------------------------------------------------- */
/*  Store                                                                      */
/* -------------------------------------------------------------------------- */

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  accessToken: null,
  refreshToken: null,
  hydrated: false,

  isAuthenticated() {
    return get().user !== null && get().accessToken !== null
  },

  async hydrate() {
    const { accessToken, refreshToken } = readTokens()

    // No tokens at all → nothing to hydrate, signal "ready" so the
    // router can render /login.
    if (accessToken === null && refreshToken === null) {
      set({ hydrated: true })
      return
    }

    // Try the access token first.
    if (accessToken !== null) {
      const decoded = decodeUser(accessToken)
      if (decoded !== null) {
        set({
          accessToken,
          refreshToken,
          user: { ...decoded, name: null, email: null },
          hydrated: true,
        })
        return
      }
    }

    // Access expired or invalid; try a silent refresh if we still
    // have a refresh token.
    if (refreshToken !== null) {
      try {
        const newAccess = await runRefresh(refreshToken)
        const decoded = decodeUser(newAccess)
        if (decoded !== null) {
          writeTokens(newAccess, refreshToken)
          set({
            accessToken: newAccess,
            refreshToken,
            user: { ...decoded, name: null, email: null },
            hydrated: true,
          })
          return
        }
      } catch {
        /* fall through to logged-out state */
      }
    }

    // Anything else → logged out.
    clearTokens()
    set({
      user: null,
      accessToken: null,
      refreshToken: null,
      hydrated: true,
    })
  },

  async login(email, password) {
    const response = await fetch(`${AUTH_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })
    const body = (await response.json()) as {
      success: boolean
      data?: {
        accessToken: string
        refreshToken: string
        user: { id: string; email: string; name: string; role: AuthUser['role']; tenantId: string }
      }
      error?: { code: string; message: string }
    }

    if (!response.ok || body.success !== true || body.data === undefined) {
      throw new ApiError(
        body.error?.code ?? 'LOGIN_FAILED',
        body.error?.message ?? 'Connexion impossible',
        response.status,
      )
    }

    writeTokens(body.data.accessToken, body.data.refreshToken)
    set({
      user: {
        id: body.data.user.id,
        tenantId: body.data.user.tenantId,
        role: body.data.user.role,
        name: body.data.user.name,
        email: body.data.user.email,
      },
      accessToken: body.data.accessToken,
      refreshToken: body.data.refreshToken,
      hydrated: true,
    })
  },

  logout() {
    clearTokens()
    set({
      user: null,
      accessToken: null,
      refreshToken: null,
      hydrated: true,
    })
  },

  async refresh() {
    const { refreshToken } = get()
    if (refreshToken === null) {
      throw new ApiError('AUTHENTICATION_REQUIRED', 'Session expirée', 401)
    }

    const newAccess = await runRefresh(refreshToken)
    writeTokens(newAccess, refreshToken)
    const decoded = decodeUser(newAccess)
    set({
      accessToken: newAccess,
      // Preserve the user's display fields if we already have
      // them; only the role/id/tenantId come from the token.
      ...(decoded !== null
        ? {
            user: {
              id: decoded.id,
              tenantId: decoded.tenantId,
              role: decoded.role,
              name: get().user?.name ?? null,
              email: get().user?.email ?? null,
            },
          }
        : {}),
    })
    return newAccess
  },
}))

/* -------------------------------------------------------------------------- */
/*  Refresh helper (extracted so `hydrate` and `refresh` share it)             */
/* -------------------------------------------------------------------------- */

async function runRefresh(refreshToken: string): Promise<string> {
  const response = await fetch(`${AUTH_BASE}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  })
  const body = (await response.json()) as {
    success: boolean
    data?: { accessToken: string; refreshToken: string }
    error?: { code: string; message: string }
  }
  if (!response.ok || body.success !== true || body.data === undefined) {
    throw new ApiError(
      body.error?.code ?? 'REFRESH_FAILED',
      body.error?.message ?? 'Session expirée',
      response.status,
    )
  }
  return body.data.accessToken
}
